import {
  Component,
  Event,
  type EventEmitter,
  h,
  Host,
  Prop,
  Listen,
  Element,
  State,
} from '@stencil/core';
import type {TabNavbarClicked} from './TabNavbarClicked';
import {SearchTab} from '../search-tab/search-tab';
import type {MoreTabsAnalyticsEvent} from './MoreTabsAnalyticsEvent';
import type {TabAnalyticsEvent} from './TabAnalyticsEvent';
import {isMobile} from '../../../utils/media';
import spinner from '@se/icons/svg/spinner.svg';
import { makeLogger } from '../../../logger/logger';
import type {DashboardTabViewV2} from '../search-tab/subcomponents/dashboard-tab-view/dashboard-tab-view-v2';
import {makeTabName} from '../search-tab/util';
import { getSiteSearchCategory } from '../../analytics-reporting/util';
import type { labelProviderInterface } from '../../LabelProviderInterface';

const { warn } = makeLogger({
  pathToCurrentFile: 'components/result-page/tabs-header/search-tabs-nav-header.tsx',
  loggersList: ['warn']
});

@Component({
  tag: 'search-tabs-nav-header',
  styleUrl: 'search-tabs-nav-header.scss',
})
export class SearchHeaderFederatedTabsComponent {
    @Element() private searchTabs: HTMLElement;
    @Prop() readonly tabs;
    @Prop() readonly tabsReflected;
    @Prop() readonly tabsPresentation: 'primary' | 'secondary';

    @Prop() readonly rtl: boolean;
    @Prop() readonly tabCounts: boolean = false;
    @Prop() readonly active;
    @Prop() readonly showTabIcon: boolean = false;
    @Prop() readonly showTab;
    @Prop({mutable: true}) tabsLimit: number = 5;//eslint-disable-line @stencil-community/strict-mutable
    @Prop() readonly savedTabLimit = this.tabsLimit;
    @Prop({mutable: true}) clickedHidedTab: boolean = false;
    @Event() tabClicked: EventEmitter<TabNavbarClicked>;
    @Prop({mutable: true}) labelProvider?: labelProviderInterface;
    @State() mobileView: boolean = isMobile();
    @State() isViewAllEvent = false;
    @Prop() readonly tabular: boolean = false;

    @Event({
      eventName: 'search:tabClick',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      tabInteractionEventEmmiter: EventEmitter<TabAnalyticsEvent>;

    @Event({
      eventName: 'search:moreTabsClick',
      composed: true,
      cancelable: true,
      bubbles: true,
    })
      moreTabsClickEventEmmiter: EventEmitter<MoreTabsAnalyticsEvent>;

    @Listen('search:dashboardViewAll', { target: 'document' })
    handleViewAllClick(event) {
        this.isViewAllEvent = event.detail?.isViewAllClicked;

        const tabName = event.detail?.tabName;
        const selectedTabElement = this.searchTabs.querySelector(`[data-tab-name="${tabName}"]`);

        selectedTabElement.scrollIntoView();
    }

    @Listen('resize', {target: 'window'})
    handleResize() {
        if (!isMobile()) {
          let visibleElementsWidth = 0;
          const hidedMenu = this.searchTabs.querySelector(
            '.dropdown-menu-tab'
          )
            ? this.searchTabs.querySelector('.dropdown-menu-tab')
              .clientWidth
            : 123;
          const tabs = this.searchTabs.querySelectorAll('.tab_visible');
          tabs.forEach((tab, index) => {
              if (index <= this.tabsLimit - 1) {
                visibleElementsWidth =
                        visibleElementsWidth + tab.clientWidth;
              }
          });
          if (window.innerWidth - hidedMenu - 100 < visibleElementsWidth) {
            if (this.tabsLimit > 1) {
              this.tabsLimit = this.tabsLimit - 1;
              this.render();
            }
          }
          if (window.innerWidth - hidedMenu - 300 > visibleElementsWidth) {
            if (this.tabsLimit < this.savedTabLimit) {
              this.tabsLimit = this.tabsLimit + 1;
              this.render();
            }
          }
        } else {
          this.tabsLimit = this.savedTabLimit;
        }
    }

    @Listen('search:replaceTabs', {target: 'document'})
    function() {
        this.replaceTabs(
          event['detail'].tab,
          event['detail'].number
        );
    }

    componentWillLoad() {
        if (!this.labelProvider) {
          this.labelProvider = (name, fallback) => {
              warn(
                'Label provider not defined for the Search Tab Nav component ',
                this,
                [name, fallback]
              );
              return fallback;
          };
        }
    }

    componentDidLoad() {
        if (!this.mobileView) {
          this.handleResize();
        }
    }

    render() {
        return (
          <Host>
            {this.tabs && this.tabs.length > 1 && (
              <div>{this.theTabs()}</div>
            )}
          </Host>
        );
    }

    private theTabs() {
        switch (this.tabsPresentation) {
        default:
        case 'primary':
          return this.renderPrimaryHeader();
        case 'secondary':
          return this.renderSecondaryHeader();
        }
    }

    private tabClasses(searchTab: SearchTab | DashboardTabViewV2, num: number) {
        const state =
            this.tabsReflected[
              searchTab.tabName || makeTabName(searchTab.tabTitle)
            ];
        const classes = [];
        classes.push(makeTabName(searchTab.tabTitle));
        if (this.clickedHidedTab) {
          classes.push(num === this.tabsLimit - 1 ? 'active' : 'inactive');
        } else {
          classes.push(num == this.active ? 'active' : 'inactive');
        }
        if (!state) classes.push('no-reflected-state');
        else {
          state['isAjaxLoading'] && classes.push('ajaxIsLoading');
          state['hasError'] && classes.push('hasError');
          state['count'] == 0 && classes.push('no-results');
        }
        classes.push('search-tabs-nav-background');

        if(this.rtl){
          classes.push('tabs--rtl');
        }
        return classes;
    }

    private title(searchTab: SearchTab | DashboardTabViewV2) {
        const tabContent = this.tabsReflected[searchTab.tabName];
        const tabName = this.getTabName(searchTab);
        const isAllResult = searchTab.tabName?.includes('/all');

        return (
          <span data-tab-name={tabContent?.tab}>
            {tabName}
            {this.tabCounts && (
              <div class={{'count': true, 'count--hidden': isAllResult}}>
                {tabContent?.count ? (
                  `(${tabContent.count})`
                ) : (
                  <div class="loading__container">
                    {tabContent?.ajaxIsLoading ? (
                      <div
                        class="loading-icon"
                        innerHTML={spinner}
                      ></div>
                    ) : (
                      <span class={'loading__no-result'}>
                                        (0)
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </span>
        );
    }

    private getTabName(searchTab: SearchTab | DashboardTabViewV2) {
        return this.labelProvider(
          'tab.' + searchTab.tabName,
          searchTab?.tabTitle || searchTab.tabName
        );
    }

    private renderPrimaryHeader() {
        const hidedMenuVisible: boolean = !this.mobileView
          ? this.tabsLimit > 0 && this.tabs.length > this.tabsLimit + 1
          : false;
        return (
          <div class="tabs__container">
            <se-tabbar option="content" color="alternative">
              <nav dir={this.rtl ? 'rtl' : 'auto'} class={this.tabular ? 'tabs__nav-tabular' : 'tabs__nav'}>
                {this.tabs.map((tab, num) => {
                    if (hidedMenuVisible) {
                      if (num < this.tabsLimit) {
                        return this.renderVisibleTabHeader(tab, num);
                      }
                    } else {
                      return this.renderVisibleTabHeader(tab, num);
                    }
                })}
                {hidedMenuVisible && this.renderDropdown()}
              </nav>
            </se-tabbar>
          </div>
        );
    }

    private renderHidedTabHeader(tab: any, num: any) {//eslint-disable-line @typescript-eslint/no-explicit-any
        return (
          <se-list-item
            data-tab-name={tab.getAttribute('tab-name') ?? undefined}
            class={`${this.tabClasses(tab, num).join(
              ' '
            )} tabs__link-dropdown`}
            onClick={() => this.replaceTabs(tab, num)}
          >
            <div class='tabs__link-title-wrapper' slot="start">{this.title(tab)}</div>
          </se-list-item>
        );
    }

    private replaceTabs(tab: any, num: any) {//eslint-disable-line @typescript-eslint/no-explicit-any
        const lastVisible = this.tabs[this.tabsLimit - 1];
        const firstHided = this.tabs[this.tabsLimit];
        const selected = this.tabs[num];
        if (num !== this.tabsLimit) {
          this.tabs[num] = firstHided;
        }
        this.tabs[this.tabsLimit - 1] = selected;
        this.tabs[this.tabsLimit] = lastVisible;
        this.tabClicked.emit({
          tab: tab,
          num: this.tabsLimit - 1,
        });

        const tabC = this.tabsReflected[tab.tabName];

        this.tabInteractionEventEmmiter.emit({
          event: 'tabClick',
          tabName: getSiteSearchCategory(tab.tabName, tab.appName),
          isHidden: true,
          position: num,
          tabCount: tabC?.count,
          category: this.isViewAllEvent ? 'tab-nav-view-all' : 'tab-nav'
        });

        this.render();
    }

    private renderVisibleTabHeader(tab: any, num: any) {//eslint-disable-line @typescript-eslint/no-explicit-any
        return (
          <a
            data-tab-name={tab.getAttribute('tab-name') ?? undefined}
            href=""
            class={`${this.tabClasses(tab, num).join(
              ' '
            )} tabs__link tab_visible`}
            onClick={(e) => {
                e.preventDefault();
                this.showSelectedTab(tab, num);
            }}
          >
            {tab.tabIcon && this.showTabIcon && (
              <span class="tabs__nav-icon">
                <se-icon>{tab.tabIcon}</se-icon>
              </span>
            )}
            <span class="tabs__header">{this.title(tab)}</span>
          </a>
        // : ''
        );
    }

    private showSelectedTab(tab: any, num: any) {//eslint-disable-line @typescript-eslint/no-explicit-any
        this.tabClicked.emit({
          tab: tab,
          num: num,
        });

        const tabC = this.tabsReflected[tab.tabName];

        this.tabInteractionEventEmmiter.emit({
          event: 'tabClick',
          tabName: getSiteSearchCategory(tab.tabName, tab.appName),
          isHidden: false,
          position: num,
          tabCount: tabC.count,
          category: this.isViewAllEvent ? 'tab-nav-view-all' : 'tab-nav'
        });

        this.clickedHidedTab = false;
    }

    private renderSecondaryHeader() {
        return (
          <se-tabbar color="alternative" option="nav">
            <div class="search-tabs">
              <nav class="search-tabs-nav-background">
                {this.tabs.map((tab, num) => {
                    return (
                      <a
                        href=""
                        class={this.tabClasses(tab, num).join(' ')}
                        onClick={(e) => {
                            e.preventDefault();
                            this.tabClicked.emit({
                              tab: tab,
                              num: num,
                            });
                            this.tabInteractionEventEmmiter.emit({
                              event: 'tabClick',
                              tabName: getSiteSearchCategory(tab.tabName, tab.appName),
                              isHidden: false,
                              position: num,
                              tabCount: tab.count,
                              category: this.isViewAllEvent ? 'tab-nav-view-all' : 'tab-nav'
                            });
                        }}
                      >
                        {tab.tabIcon && (
                          <span class="tabs__nav-icon">
                            <se-icon>{tab.tabIcon}</se-icon>
                          </span>
                        )}
                        <span class="tab-title">
                          {this.title(tab)}
                        </span>
                      </a>
                    );
                })}
              </nav>
            </div>
          </se-tabbar>
        );
    }

    private renderDropdown = () => {
        return (
          <se-dropdown
            class={`tab_hided dropdown-menu-tab${
              this.tabCounts ? '--tabs-count' : ''
            }`}
            color="none"
            maxWidth="331px"
          >
            <div
              slot="trigger"
              class="dropdown-menu-button"
            >
              <a
                href=""
                class={`dropdown-menu__link${
                  this.tabCounts ? '--tabs-count' : ''
                }`}
                onClick={(e) => {
                    e.preventDefault();
                    this.moreTabsClickEventEmmiter.emit(
                      {
                        event: 'moreTabsClick',
                      }
                    );
                }}
              >
                <se-icon>other_vertical</se-icon>
                <span class="dropdown-menu-button__text">
                  {this.labelProvider(
                    'tab.more-button',
                    'More'
                  )}
                </span>
              </a>
            </div>

            <se-list option="dropdown" class="dropdown-menu-list">
              {this.tabs.map((tab, num) => {
                  if (num >= this.tabsLimit) {
                    return this.renderHidedTabHeader(
                      tab,
                      num
                    );
                  }
              })}
            </se-list>
          </se-dropdown>);
    };
}
